import React from 'react';
import { ContainerComponent, wrap } from 'AppReact';
import Controller from './controller';
import { ClearAllHeader, GroupButton, Property, PropertyButton } from './molecules';
import { ButtonListWrapper, GroupLabelWrapper } from './wrappers';
import { Caret } from './atoms';
import { DropdownSearchInput } from 'molecules';
import { Label, FilterCounter } from 'atoms';
import {
  FilterHeaderWrapper, StaticWrapper, FlexCenterWrapper, ScrollableDropdownBodyWrapper, DropdownContentWrapper,
  GenericWrapper, FullWidthFlexboxCentered, CustomBackgroundWrapper
} from 'wrappers';
import { DownCaretSvg } from 'components/icons';
import getIsMobile from "components/atoms/utils/getIsMobile";
import internalState from './internalState';

const isMobile = getIsMobile();

const containerRef = React.createRef();
const headerRef = React.createRef();
const inputRef = React.createRef();

export const definition = {
  name: 'MultiPropertySelector',
  controller: Controller,
  renderIf: ({ }) => window?.properties?.length > 1,
  initialProps: ({
    properties, groups, selectedPropertyIds, selectedGroupIds, scrollLeft, label, fullWidth, silentUpdate
  }) => ({
    properties, groups, selectedPropertyIds, selectedGroupIds, label,
    left: (headerRef?.current?.offsetLeft ?? 0) - (scrollLeft ?? 0),
    isReadonly: properties.length === 1,
    fullWidth, silentUpdate
  }),
  props: internalState,
  elements: [
    {
      wrapper: FilterHeaderWrapper,
      name: 'Selector',
      ref: headerRef,
      isButton: true,
      bindings: ({ isOpen, isReadonly, selectedPropertyIds, selectedGroupIds, silentUpdate }) => ({ isOpen, containerRef, isReadonly, inputRef, selectedPropertyIds, selectedGroupIds, silentUpdate }),
      elements: [
        {
          wrapper: CustomBackgroundWrapper,
          displayState: ({ }) => ({ alignItems: 'center', width: 'calc(100% - 42px)' }),
          name: 'LeftSide',
          elements: [
            {
              component: Label,
              name: 'Label',
              displayValue: ({ label }) => label ? label : 'Filter by property: ',
              bindings: ({ }) => ({})
            },
            {
              wrapper: ButtonListWrapper,
              displayState: ({ fullWidth }) => ({ fullWidth }),
              name: 'ButtonList',
              elements: [
                {
                  component: PropertyButton,
                  name: 'PropertyButton',
                  isListOf: 'selectedPropertyIds',
                  renderIf: ({ selectedPropertyId, properties }) => {
                    let property = properties.find(property => property.id === selectedPropertyId);
                    return !!property;
                  },
                  bindings: ({ selectedPropertyId, properties, selectedPropertyIds, selectedGroupIds, isReadonly, silentUpdate }) => {
                    let property = properties.find(property => property.id === selectedPropertyId);
                    return { icon: property.icon, id: selectedPropertyId, selectedPropertyIds, selectedGroupIds, isReadonly, silentUpdate }
                  },
                },
                {
                  component: GroupButton,
                  name: 'GroupButton',
                  isListOf: 'selectedGroupIds',
                  renderIf: ({ selectedGroupId, groups }) => {
                    let group = groups.find(group => group.id === selectedGroupId);
                    return !!group;
                  },
                  bindings: ({ selectedGroupId, groups, selectedGroupIds, selectedPropertyIds, silentUpdate }) => {
                    let group = groups.find(group => group.id === selectedGroupId);
                    return { name: group.name, id: selectedGroupId, selectedGroupIds, selectedPropertyIds, silentUpdate }
                  },
                },
              ]
            }
          ]
        },
        {
          component: FilterCounter,
          name: 'Counter',
          displayValue: ({ selectedPropertyIds, selectedGroupIds }) => {
            const numSelected = (selectedPropertyIds?.length ?? 0) + (selectedGroupIds.length ?? 0);
            return numSelected;
          },
        },
        {
          component: Caret,
          name: 'DownCaretSvg',
          displayValue: () => <DownCaretSvg />,
          bindings: ({ }) => ({})
        }
      ]
    },
    {
      wrapper: ScrollableDropdownBodyWrapper,
      name: 'Body',
      ref: containerRef,
      displayState: ({ left, fullWidth }) => ({ left: isMobile ? '55' : left, width: fullWidth ? '100%' : '' }),
      renderIf: ({ isOpen }) => isOpen,
      elements: [
        {
          component: ClearAllHeader,
          name: 'ClearAllHeader',
          bindings: ({ selectedPropertyIds, properties, groups }) => ({ selectedPropertyIds, properties, groups })
        },
        {
          wrapper: GenericWrapper,
          name: 'SearchWrapper',
          ref: ({ }) => inputRef ?? null,
          elements: [
            {
              component: DropdownSearchInput,
              name: 'Search',
              bindings: ({ searchTerm }) => ({ value: searchTerm, placeholder: "Search" })
            },
          ]
        },
        {
          wrapper: DropdownContentWrapper,
          name: 'Properties',
          elements: [
            {
              component: Property,
              name: 'Property',
              isListOf: 'properties',
              renderIf: ({ property, searchTerm }) => (new RegExp(searchTerm, 'i')).test(property.name),
              bindings: ({ property, selectedPropertyIds, selectedGroupIds }) => {
                const isSelected = (selectedPropertyIds || []).includes(property.id);
                return { icon: property.icon, propertyName: property.name, id: property.id, isSelected, selectedPropertyIds, selectedGroupIds }
              }
            },
            {
              component: wrap(Label, GroupLabelWrapper),
              name: 'GroupSeparator',
              bindings: () => { },
              renderIf: ({ groups, searchTerm }) => {
                const availableGroups = groups.filter(g => (new RegExp(searchTerm, 'i')).test(g.name))
                return availableGroups.length > 0;
              },
              displayValue: () => 'GROUPS'
            },
            {
              component: Property,
              name: 'Group',
              isListOf: 'groups',
              renderIf: ({ group, searchTerm }) => (new RegExp(searchTerm, 'i')).test(group.name),
              bindings: ({ group, selectedGroupIds, selectedPropertyIds }) => {
                const isSelected = selectedGroupIds.includes(group.id);
                return { icon: '', propertyName: group.name, id: group.id, isSelected, selectedGroupIds, group: true, selectedPropertyIds }
              }
            }
          ]
        }
      ]
    }
  ]
}

const MultiPropertySelector = ContainerComponent(definition)

export default wrap(MultiPropertySelector, StaticWrapper);

